import instantsearch from 'instantsearch.js/dist-es5-module';
import { isAlgoliaOn } from './Utils';

const clone = require('lodash/clone');
const merge = require('lodash/merge');

const mainStoreName = 'nav';
const mainHitsId = `ais-${mainStoreName}-hits`;
const mainPanelId = `ais-${mainStoreName}-panel`;
const popinRefineById = 'aisPopinRefineBy';

const dicoCurrencyKey = 'currencySymbol';

const widgets = instantsearch.widgets;
const numericFacetsStats = {};
const storeModel = {
  helper: {},
  defaultIndex: '',
  literalFacets: [],
  currencySymbol: '',
  UIState: '',
  indexKeyList: [],
};

// Constant url params names
const urlKeys = {
  query: 'search',
  index: 'ais-i',
  page: 'ais-p',
  literalRefinements: 'ais-lref',
  numericRefinements: 'ais-nref',
};

/* UI States */
const states = {
  stop: 'stop',
  init: 'init',
  run: 'run',
  sleep: 'sleep',
  reset: 'reset',
};

/* UI Events */
const events = {
  start: 'algolia-start',
  close: 'algolia-close',
  search: 'algolia-search',
};

/* Constant refinements */
const defaultRefinementKeys = ['online_date', 'offline_date'];

const aisStartEvent = events.start;
const aisCloseEvent = events.close;
const aisSearchEvent = events.search;

let literalRefinementCount = 0;
let numericRefinementCount = 0;
let defaultStoreConf = {};

/* Dico related props */
let dicoCurrency = '';

let apiConfig;
let apiKey;
let appID;
let numericFacets;
let indexList;
let mainStore;

function setupAlgoliaInfos(/* name */) {
  try {
    if (!$.isEmptyObject(window.algolia)) {
      /* Check API credential */
      if (typeof apiConfig === 'undefined') {
        apiConfig = window.algolia;
        apiKey = apiConfig.searchApiKey;
        appID = apiConfig.applicationId;
        numericFacets = apiConfig.attributesForNumericFacets;
        indexList = apiConfig.sortByList;
      }

      /* Check dicokey setting */
      if (dicoCurrency === '') {
        if (typeof OCMS.aisPanel.dicoKeys[dicoCurrencyKey] !== 'undefined') {
          dicoCurrency = OCMS.aisPanel.dicoKeys[dicoCurrencyKey];
        } else {
          throw new Error('Missing currency info in dictionnary.');
        }
      }

      /* Check default config */
      if ($.isEmptyObject(defaultStoreConf)) {
        const currDate = Math.round(Date.now() * 0.001);

        try {
          dicoCurrency = OCMS.aisPanel.dicoKeys[dicoCurrencyKey];
        } catch (error) {
          console.error('===== Missing dicoKeys for Algolia =====', error);
        }

        defaultStoreConf = {
          appId: appID,
          apiKey: apiKey,
          indexName: indexList[0].index,
          searchParameters: {
            hitsPerPage: 18,
            numericRefinements: {
              online_date: {
                '<=': [currDate],
              },
              offline_date: {
                '>=': [currDate],
              },
            },
            disjunctiveFacets: numericFacets,
            facetingAfterDistinct: true,
            facets: numericFacets,
            maxValuesPerFacet: 100,
            analytics: false,
          },
        };

        Object.freeze(defaultStoreConf);
      }
    } else {
      throw new Error('Missing Algolia configuration infos.');
    }
  } catch (error) {
    console.error('AIS infos setup fail.', error.message);
  }
}

function setupAlgoliaStore(name) {
  /* Perform specific store configuration step */
  if (typeof window.algoliaStores[name] === 'undefined') {
    /* Extend static model with initial data */
    window.algoliaStores[name] = merge(clone(storeModel), {
      UIState: states.init,
      currencySymbol: dicoCurrency,
      defaultIndex: indexList[0].index,
    });
  }

  return window.algoliaStores[name];
}

function getIndexKeyList() {
  const resultList = [];
  /* Catch if the configured default index exist */
  if (typeof indexList !== 'undefined' && indexList.length > 0) {
    /* Build an ordered list of index keys at startup */
    indexList.forEach((elem) => {
      resultList.push(elem.key);
    });
  } else {
    console.error('Missing index key configuration.');
  }

  return resultList;
}

/* Get only literal facets in a distinct property */
function setupLiteralFacet() {
  let isLiteral = false;

  if (typeof window.algoliaStores[mainStoreName].helper !== 'undefined') {
    window.algoliaStores[mainStoreName].helper.state.disjunctiveFacets.forEach((fKey) => {
      if (numericFacets.indexOf(fKey) === -1) {
        window.algoliaStores[mainStoreName].literalFacets.push(fKey);
        isLiteral = true;
      }
    });

    /* Lock the setup property */
    Object.freeze(window.algoliaStores[mainStoreName].literalFacets);
  }
  return isLiteral;
}

/* Compute the amount of "literal refinements" apply on a given state */
function countLiteralRefinements() {
  let literalTotal = 0;

  if (typeof window.algoliaStores[mainStoreName].helper !== 'undefined') {
    window.algoliaStores[mainStoreName].literalFacets.forEach((fKey) => {
      literalTotal += window.algoliaStores[mainStoreName].helper.state.getDisjunctiveRefinements(fKey).length;
    });
  }
  literalRefinementCount = literalTotal;
}

/* Compute the amount of "numeric refinements" apply on a given state */
function countNumericRefinements() {
  let numericTotal = 0;

  if (typeof window.algoliaStores[mainStoreName].helper !== 'undefined') {
    numericFacets.forEach((fKey) => {
      if (defaultRefinementKeys.indexOf(fKey) === -1) {
        const tempNumFacet = window.algoliaStores[mainStoreName].helper.state.getNumericRefinements(fKey);

        Object.keys(tempNumFacet).forEach((op) => {
          numericTotal += tempNumFacet[op].length;
        });
      }
    });
  }
  numericRefinementCount = numericTotal;
}

function updateFacetsStats(results) {
  /* Setup global numeric stats per facet */
  numericFacets.forEach((fKey) => {
    if (defaultRefinementKeys.indexOf(fKey) === -1) {
      numericFacetsStats[fKey] = results.getFacetStats(fKey);
    }
  });

  /* Compute the amount of "refinements" use for filtering */
  countLiteralRefinements();
  countNumericRefinements();
}

function analyticsOnFor(store) {
  store.helper.setQueryParameter('analytics', true);
}

/* Make the first call to Algolia service */
function mainStoreStartup() {
  mainStore.storeInstance.start();
  mainStore.helper = mainStore.storeInstance.helper; // NOTICE : helper property doesn't exist before the first start() call
  mainStore.helper.once('result', () => analyticsOnFor(mainStore));
  mainStore.helper.on('result', updateFacetsStats);
}

function stopOnceEventPropagation(...rest) {
  rest[0].stopImmediatePropagation();
  window.removeEventListener('pageshow', stopOnceEventPropagation);
}

/* Register events related to store connection and widgets init phase */
function mainStoreEventSetup() {
  /* TEMP fix to recover from searchValue reset */
  window.addEventListener('pageshow', stopOnceEventPropagation);

  $(document).one(aisStartEvent, mainStoreStartup);

  /* Keeping track of non numeric facet base on first response */
  mainStore.storeInstance.once('render', setupLiteralFacet);
}

/* Return an Algolia store Instance */
function init(conf) {
  const tempConf = (typeof conf !== 'undefined') ? merge(clone(defaultStoreConf), conf) : clone(defaultStoreConf);

  let store;

  /* Check the ability to init the store instance */
  if ($.isEmptyObject(tempConf) === false) {
    store = instantsearch(tempConf);
  } else {
    store = null;
  }
  return store;
}

function getMainStoreInstance() {
  if (typeof window.algoliaStores === 'undefined' || typeof window.algoliaStores[mainStoreName] === 'undefined') {
    const storeTag = { searchParameters: { analyticsTags: mainStoreName } };

    /* Prevent any future update of the object */
    Object.freeze(urlKeys);

    /* Init Store */
    window.algoliaStores = {};
    mainStore = window.algoliaStores[mainStoreName] = setupAlgoliaStore(mainStoreName);
    mainStore.storeInstance = init(storeTag);
    mainStore.indexKeyList = getIndexKeyList();

    /* Set the parameters as immutable (values of properties stay writable) */
    Object.seal(mainStore);
    /* Lock the setup property */
    Object.freeze(mainStore.indexKeyList);

    /* Setup global store events */
    mainStoreEventSetup();
  } else {
    mainStore = window.algoliaStores[mainStoreName];
  }

  return mainStore.storeInstance;
}

function getNewStore(conf) {
  /* Always init  a new Store */
  return init(conf);
}

/* Return the requested index, all if no key in argument */
function getIndex(name, indexKey) {
  return (typeof indexKey !== 'undefined') ? indexList[window.algoliaStores[name].indexKeyList.indexOf(indexKey)] : indexList;
}

/* Return key name of the index in argument */
function getIndexKey(index) {
  let iKey = '';

  if (typeof index !== 'undefined') {
    indexList.forEach((elem) => {
      if (elem.index === index) { iKey = elem.key; }
    });
  }

  return iKey;
}

/* Return the index keys currently configure */
function getAllIndexKeys(name) {
  return window.algoliaStores[name].indexKeyList;
}

function getDefaultIndex(name) {
  return window.algoliaStores[name].defaultIndex;
}

function isNumericFacet(facetKey) {
  return (numericFacets.indexOf(facetKey) !== -1);
}

function getNumericFacetsStats(facetKey) {
  return ((typeof facetKey !== 'undefined') && isNumericFacet(facetKey)) ? numericFacetsStats[facetKey] : numericFacetsStats;
}

/* Export Algolia client helper (allow a better event tuning) */
function getMainHelper() {
  return window.algoliaStores[mainStoreName].helper;
}

function getCurrencySymbol(name) {
  return window.algoliaStores[name].currencySymbol;
}

function getUrlKeys() {
  return urlKeys;
}

function getTotalRefinements() {
  return literalRefinementCount + numericRefinementCount;
}

function getTotalHits(name) {
  return (typeof window.algoliaStores[name].helper !== 'undefined') ? window.algoliaStores[name].helper.lastResults.nbHits : 0;
}

function getCurrentQuery(name) {
  return (typeof window.algoliaStores[name].helper !== 'undefined') ? window.algoliaStores[name].helper.getQuery().query : '';
}

function getAISUrlParameter(param) {
  const fParam = param.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');
  const regex = new RegExp(`[\\?&]${fParam}=([^&#]*)`);
  const results = regex.exec(location.search);
  return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
}

/**
* Call as a callback in helper.clearRefinements() (e.g. algolia-search-box widget) method, to reset only the temporary refinements.
* For each refinement found in helper state, a call to this function happen with :
*
* @param {Object} value : An object that represent the info of the applied refinement.
* @param {String} attribute  : The facet name
* @param {String} type  : The facet type (disjunctiveFacet, numeric etc...)
*
* @return {Boolean} When the returned result is equal to "true", the refinement is remove from the state.
*/
function clearRefinementHandler(value, attribute) {
  return (defaultRefinementKeys.indexOf(attribute) === -1);
}

function isDefaultRefinements(attribute) {
  return (defaultRefinementKeys.indexOf(attribute) !== -1);
}

function isAISStop(name) {
  return (window.algoliaStores[name].UIState === states.stop);
}

function isAISInit(name) {
  return (window.algoliaStores[name].UIState === states.init);
}

function isAISRun(name) {
  return (window.algoliaStores[name].UIState === states.run);
}

function isAISSleep(name) {
  return (window.algoliaStores[name].UIState === states.sleep);
}

function isAISReset(name) {
  return (window.algoliaStores[name].UIState === states.reset);
}

function setAISStop(name) {
  window.algoliaStores[name].UIState = states.stop;
}

function setAISInit(name) {
  window.algoliaStores[name].UIState = states.init;
}

function setAISRun(name) {
  window.algoliaStores[name].UIState = states.run;
}

function setAISSleep(name) {
  window.algoliaStores[name].UIState = states.sleep;
}

function setAISReset(name) {
  window.algoliaStores[name].UIState = states.reset;
}

/* Init default store configuration */
if (isAlgoliaOn()) {
  /* Make configuration object immutable for the rest of the execution time */
  Object.freeze(window.algolia);
  /* Make base model non-extensible */
  Object.freeze(storeModel);

  setupAlgoliaInfos();
}

export {
  setupAlgoliaStore,
  getMainStoreInstance,
  getTotalRefinements,
  getCurrencySymbol,
  getNumericFacetsStats,
  getAISUrlParameter,
  isNumericFacet,
  getDefaultIndex,
  getAllIndexKeys,
  getIndexKey,
  getMainHelper,
  mainStoreName,
  getTotalHits,
  getUrlKeys,
  getIndex,
  getNewStore,
  widgets,
  setAISStop,
  setAISInit,
  setAISRun,
  setAISSleep,
  setAISReset,
  isAISStop,
  isAISInit,
  isAISRun,
  isAISSleep,
  isAISReset,
  popinRefineById,
  mainPanelId,
  mainHitsId,
  aisStartEvent,
  aisCloseEvent,
  aisSearchEvent,
  clearRefinementHandler,
  isDefaultRefinements,
  updateFacetsStats,
  analyticsOnFor,
  getCurrentQuery,
};



// WEBPACK FOOTER //
// ./js/common/Algolia.js